/**
 * Falls back to the code used in `javascripts/behaviors/copy_to_clipboard.js`
 *
 * Renders a button with a clipboard icon that copies the content of `data-clipboard-text`
 * when clicked.
 *
 * @example
 * <clipboard-button
 *   title="Copy"
 *   text="Content to be copied"
 *   css-class="gl-border-0 gl-bg-transparent"
 * />
 */ import { GlButton, GlTooltipDirective } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { uniqueId } from 'lodash-es';
import { sanitize } from '~/lib/dompurify';
import { BV_HIDE_TOOLTIP, BV_SHOW_TOOLTIP } from '~/lib/utils/constants';
import { __ } from '~/locale';
import { CLIPBOARD_SUCCESS_EVENT, CLIPBOARD_ERROR_EVENT, I18N_ERROR_MESSAGE } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/behaviors/copy_to_clipboard.js?vue3';
import { glListenersMixin } from '~/lib/utils/vue3compat/gl_listeners_mixin';
import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
export default {
    name: 'ClipboardButton',
    i18n: {
        copied: __('Copied'),
        error: I18N_ERROR_MESSAGE
    },
    CLIPBOARD_SUCCESS_EVENT,
    CLIPBOARD_ERROR_EVENT,
    directives: {
        GlTooltip: GlTooltipDirective
    },
    components: {
        GlButton
    },
    mixins: [
        glListenersMixin,
        glSlotsMixin
    ],
    props: {
        text: {
            type: String,
            required: true
        },
        gfm: {
            type: String,
            required: false,
            default: null
        },
        title: {
            type: String,
            required: true
        },
        tooltipPlacement: {
            type: String,
            required: false,
            default: 'top'
        },
        tooltipContainer: {
            type: [
                String,
                Boolean
            ],
            required: false,
            default: false
        },
        tooltipBoundary: {
            type: String,
            required: false,
            default: null
        },
        cssClass: {
            type: String,
            required: false,
            default: null
        },
        category: {
            type: String,
            required: false,
            default: 'secondary'
        },
        size: {
            type: String,
            required: false,
            default: 'medium'
        },
        variant: {
            type: String,
            required: false,
            default: 'default'
        },
        icon: {
            type: String,
            required: false,
            default: 'copy-to-clipboard'
        }
    },
    data () {
        return {
            localTitle: this.title,
            titleTimeout: null,
            id: null
        };
    },
    computed: {
        clipboardText () {
            if (this.gfm !== null) {
                return JSON.stringify({
                    text: this.text,
                    gfm: this.gfm
                });
            }
            return this.text;
        },
        tooltipDirectiveOptions () {
            return {
                placement: this.tooltipPlacement,
                container: this.tooltipContainer,
                boundary: this.tooltipBoundary
            };
        },
        sanitizedLocalTitle () {
            return sanitize(this.localTitle);
        }
    },
    created () {
        this.id = uniqueId('clipboard-button-');
    },
    methods: {
        updateTooltip (title) {
            this.localTitle = title;
            this.$root.$emit(BV_SHOW_TOOLTIP, this.id);
            clearTimeout(this.titleTimeout);
            this.titleTimeout = setTimeout(()=>{
                this.localTitle = this.title;
                this.$root.$emit(BV_HIDE_TOOLTIP, this.id);
            }, 1000);
        }
    }
};
